import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {codeScanningAlertPath} from '@github-ui/paths'
import type {CodeScanningSecurityAlertStatus} from '@github-ui/server-persisted-queries/types/IssueViewerSecondaryViewServerQuery'
import type {IconProps} from '@primer/octicons-react'
import {ShieldCheckIcon, ShieldIcon, ShieldSlashIcon} from '@primer/octicons-react'
import {ActionList, Link} from '@primer/react'
import {clsx} from 'clsx'
import type {FC} from 'react'

import type {
  IssueSecondarySecurityAlertNode,
  IssueSecondarySecurityAlertOwnerNode,
  IssueSecondarySecurityAlertRepositoryNode,
} from './linked-security-alert-query-types'
import styles from './LinkedSecurityAlert.module.css'

type LinkedSecurityAlertFragment = Pick<IssueSecondarySecurityAlertNode, 'number' | 'status' | 'title'> & {
  readonly repository:
    | (Pick<IssueSecondarySecurityAlertRepositoryNode, 'name'> & {
        readonly owner: Pick<IssueSecondarySecurityAlertOwnerNode, 'login'>
      })
    | null
}

type LinkedSecurityAlertProps = {
  alert: LinkedSecurityAlertFragment
}

type LinkedSecurityAlertLinkItemProps = LinkedSecurityAlertProps & {
  isSmall?: boolean
}

export const LinkedSecurityAlertLinkItem: FC<LinkedSecurityAlertLinkItemProps> = ({alert, isSmall}) => {
  const getHovercardAttributesForType = useGetHovercardAttributesForType()

  if (!alert.repository || !alert.number) {
    return null
  }

  const owner = alert.repository.owner.login
  const repoName = alert.repository.name

  const alertUrl = codeScanningAlertPath({
    owner,
    repo: repoName,
    alertNumber: alert.number,
  })

  return (
    <Link
      href={alertUrl}
      {...(!isSmall
        ? getHovercardAttributesForType('security_alert', {owner, repo: repoName, alertNumber: alert.number})
        : {})}
      target="_blank"
      role="link"
      className={clsx(styles.securityAlertLink)}
    >
      <LinkedSecurityAlertIcon alert={alert} />
      {!isSmall ? <LinkedAlertName alert={alert} /> : <span>{`Alert: #${alert.number}`}</span>}
    </Link>
  )
}

export const LinkedSecurityAlertListItem: FC<LinkedSecurityAlertProps> = ({alert}) => {
  if (!alert.repository || !alert.number) {
    return null
  }

  const owner = alert.repository.owner.login
  const repoName = alert.repository.name

  const alertUrl = codeScanningAlertPath({
    owner,
    repo: repoName,
    alertNumber: alert.number,
  })

  return (
    <ActionList.LinkItem href={alertUrl} target="_blank">
      <ActionList.LeadingVisual>
        <LinkedSecurityAlertIcon alert={alert} />
      </ActionList.LeadingVisual>
      <LinkedAlertName alert={alert} expandedTitle />
    </ActionList.LinkItem>
  )
}

const alertIconConfig = {
  FIXED: {Icon: ShieldCheckIcon, className: 'fgColor-done'},
  OPEN: {Icon: ShieldIcon, className: 'fgColor-open'},
  DISMISSED: {Icon: ShieldSlashIcon, className: 'fgColor-closed'},
} as const satisfies Partial<Record<CodeScanningSecurityAlertStatus, {Icon: FC<IconProps>; className: string}>>

export const LinkedSecurityAlertIcon: FC<Pick<LinkedSecurityAlertProps, 'alert'> & IconProps> = ({alert, ...props}) => {
  const status = alert.status && alert.status in alertIconConfig ? alert.status : 'OPEN'
  const {Icon, className} = alertIconConfig[status]

  return <Icon className={className} {...props} />
}

export const LinkedAlertName: FC<LinkedSecurityAlertProps & {expandedTitle?: boolean}> = ({alert, expandedTitle}) => {
  return (
    <div className={clsx(styles.titleContainer)}>
      <span className={clsx(styles.alertTitle, expandedTitle && styles.listItemTitle)}>{`Alert: ${alert.title}`}</span>
      <span className={styles.alertNumber}>#{alert.number}</span>
    </div>
  )
}
